import { isInvalidString, isInvalidPurchaseOrderNumber, isInvalidQuantity } from "src/validation/stringValidation";
import { isInvalidISODate } from "src/helpers/dateHelper";
import { FormValidator } from "../formValidator";
import { ValidationError, ValidationErrors } from "src/errors";
import { CreateChildPurchaseOrderFormDataKey, ChildPOProductInputField } from "src/types";

class CreateChildPurchaseOrderFormValidator extends FormValidator {
  private static validatePurchaseOrderNumber(value: unknown): ValidationError<"parentPurchaseOrderId"> | undefined {
    return isInvalidPurchaseOrderNumber(value)
      ? new ValidationError("Enter a valid purchase order number (e.g., 'PO-2055')", "parentPurchaseOrderId")
      : undefined;
  }

  private static validateEstDeliveryDate(value: unknown): ValidationError<"estDeliveryDate"> | undefined {
    if (!value || isInvalidISODate(value)) {
      return new ValidationError("Enter a valid delivery date (example format: '01/01/2026')", "estDeliveryDate");
    }
    return undefined;
  }

  private static validateProducts(value: unknown): ValidationError<"products"> | undefined {
    if (!Array.isArray(value) || !value.length) {
      return new ValidationError("At least one product is required", "products");
    }

    const productsWithSplitQty = value.filter((p: ChildPOProductInputField) => {
      const uk = parseInt(p.ukQuantity, 10) || 0;
      const eu = parseInt(p.euQuantity, 10) || 0;
      return uk + eu > 0;
    });

    if (productsWithSplitQty.length === 0) {
      return new ValidationError("At least one product must have a UK or EU quantity greater than zero", "products");
    }

    for (const arrayValue of value) {
      const { id, ukQuantity, euQuantity } = arrayValue as ChildPOProductInputField;
      const uk = parseInt(ukQuantity, 10) || 0;
      const eu = parseInt(euQuantity, 10) || 0;

      if (uk + eu <= 0) {
        continue;
      }

      if (!id || id.trim() === "") {
        return new ValidationError(`Product ID cannot be empty`, "products");
      }

      if (isInvalidString(id, 5, 15)) {
        return new ValidationError(`Product ID must be between 5 and 15 characters (ID: ${id})`, "products");
      }

      const ukStr = typeof ukQuantity === "string" ? ukQuantity.trim() : "";
      const euStr = typeof euQuantity === "string" ? euQuantity.trim() : "";
      if (ukStr !== "" && isInvalidQuantity(ukStr, 0, 100000)) {
        return new ValidationError(`UK quantity must be a whole number between 0 and 100000 (ID: ${id})`, "products");
      }
      if (euStr !== "" && isInvalidQuantity(euStr, 0, 100000)) {
        return new ValidationError(`EU quantity must be a whole number between 0 and 100000 (ID: ${id})`, "products");
      }
    }

    return undefined;
  }

  private static validateOperationsNote(value: unknown): ValidationError<"operationsNote"> | undefined {
    if (value && typeof value !== "string") {
      return new ValidationError("Operations note must be a string", "operationsNote");
    }
    return undefined;
  }

  private static readonly validationStrategies: Record<
    CreateChildPurchaseOrderFormDataKey,
    (inputValue: unknown) => ValidationError<CreateChildPurchaseOrderFormDataKey> | undefined
  > = {
    parentPurchaseOrderId: CreateChildPurchaseOrderFormValidator.validatePurchaseOrderNumber,
    products: CreateChildPurchaseOrderFormValidator.validateProducts,
    estDeliveryDate: CreateChildPurchaseOrderFormValidator.validateEstDeliveryDate,
    operationsNote: CreateChildPurchaseOrderFormValidator.validateOperationsNote
  };

  static validatePurchaseOrderFormData<T extends CreateChildPurchaseOrderFormDataKey>(
    formData: Partial<Record<T, unknown>>
  ): ValidationErrors<T> {
    return super.validateFormData(
      formData,
      CreateChildPurchaseOrderFormValidator.validationStrategies
    ) as ValidationErrors<T>;
  }
}

export { CreateChildPurchaseOrderFormValidator };
